import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useNotification } from '@/shared/context/NotificationContext';
import { ModalCloseButton } from '@/shared/components/ModalCloseButton';
import { DialogShell } from '@/shared/components/DialogShell';
import { buildWorkQrUrl } from '@/features/qr/utils/qrUrl';
import { logger } from '@/shared/utils/logger';

interface WorkQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  pwaUrlBase: string;
  exhibitionId: string;
  workId: string;
  workName: string;
}

export const WorkQrModal: React.FC<WorkQrModalProps> = ({
  isOpen,
  onClose,
  pwaUrlBase,
  exhibitionId,
  workId,
  workName,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const qrUrl = buildWorkQrUrl(pwaUrlBase, exhibitionId, workId);
  const titleId = 'work-qr-modal-title';

  useEffect(() => {
    if (!isOpen) {
      setQrDataUrl(null);
      return;
    }

    let cancelled = false;

    const generate = async () => {
      setIsGenerating(true);
      try {
        const dataUrl = await QRCode.toDataURL(qrUrl, {
          width: 256,
          margin: 2,
          color: {
            dark: '#231c16',
            light: '#ffffff',
          },
        });
        if (!cancelled) {
          setQrDataUrl(dataUrl);
        }
      } catch (error) {
        logger.error('Failed to generate work QR code', error);
        if (!cancelled) {
          setQrDataUrl(null);
          showError(t('qrCode.generationError'));
        }
      } finally {
        if (!cancelled) {
          setIsGenerating(false);
        }
      }
    };

    generate();

    return () => {
      cancelled = true;
    };
  }, [isOpen, qrUrl, showError, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      showSuccess(t('works.qrCode.linkCopied'));
    } catch (error) {
      logger.error('Failed to copy QR URL', error);
      showError(t('works.qrCode.copyError'));
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-${workId}.png`;
    link.click();
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      titleId={titleId}
      overlayClassName="work-qr-modal-overlay"
      dialogClassName="work-qr-modal"
    >
      <ModalCloseButton onClick={onClose} className="work-qr-modal-close" />
      <h2 id={titleId} className="work-qr-modal-title">
        {t('works.qrCode.modalTitle')}
      </h2>
      {workName && <p className="work-qr-modal-work-name">{workName}</p>}

      <div className="work-qr-modal-qr">
        {isGenerating && <p className="work-qr-modal-loading">{t('common.loading')}</p>}
        {!isGenerating && qrDataUrl && (
          <img
            src={qrDataUrl}
            alt={t('works.qrCode.previewAlt', { name: workName || workId })}
            className="work-qr-modal-image"
            width={256}
            height={256}
          />
        )}
      </div>

      <p className="work-qr-modal-url" tabIndex={0}>
        {qrUrl}
      </p>

      <div className="work-qr-modal-actions">
        <button
          type="button"
          className="work-qr-copy-btn"
          onClick={handleCopy}
          aria-label={t('works.qrCode.copyLink')}
        >
          {t('works.qrCode.copyLink')}
        </button>
        <button
          type="button"
          className="work-qr-download-btn"
          onClick={handleDownload}
          disabled={!qrDataUrl}
          aria-label={t('works.qrCode.download')}
        >
          {t('works.qrCode.download')}
        </button>
      </div>
    </DialogShell>
  );
};
